'use client'

// App Router page_view emitter. Pages Router routes get page_view from
// pages/_app.js via Router.events; App Router routes (/setups, /signup,
// /stories, /[[...slug]]) don't fire those events, so we need a separate
// hook keyed on next/navigation's usePathname + useSearchParams.
//
// Fires on:
//  - initial mount
//  - every pathname or searchParams change
//
// De-duplication vs. the Pages Router listener isn't a concern in practice:
// each route is served by exactly one router (a route either has an
// app/*/page.js or a pages/*.js, never both), so at most one page_view fires
// per navigation.

import { Suspense, useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { pushDataLayerEvent } from '../utils/dataLayer'
import { captureAttributionOnLoad } from '../utils/attribution'

// useSearchParams() in App Router client components deopts the whole tree to
// client rendering unless wrapped in <Suspense>. Boundary here so the layout
// stays server-rendered for everything else.
function PageViewEmitter() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastKey = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const search = searchParams ? searchParams.toString() : ''
    const key = pathname + (search ? '?' + search : '')
    // Guard: React 18 double-mount in dev + occasional identical re-renders
    if (lastKey.current === key) return
    lastKey.current = key

    pushDataLayerEvent('page_view', {
      page_location: window.location.href,
      page_path: pathname || window.location.pathname,
      page_referrer: document.referrer,
      page_title: document.title,
    })

    // First-touch attribution capture — if this landing URL carries utm_* or
    // click-ID params AND we don't already have a valid stored first-touch,
    // stash it in localStorage for 30 days. All downstream Purchase /
    // Subscribe events + CTA-links to the members app pick it up from there.
    captureAttributionOnLoad()
  }, [pathname, searchParams])

  return null
}

export default function AppRouterPageView() {
  return (
    <Suspense fallback={null}>
      <PageViewEmitter/>
    </Suspense>
  )
}
